'use strict';
/*
==================
       NOTES
==================
* When we want to save equations differentiating scenarios, we should:
  1. Add a scenario dropdown (getting scenarios from API)
  2. Assign to a variable the scenarioID selected when user changes the value of the dropdown
  3. On Save, pass the scenarioID as a param to the db request
  4. Create a new column in the period equations database "scenarioID"
  5. Adapt store procedures to receive and perform operations filtering also by scenarioID (on Update and on Delete)
  6. Test that all works as expected
*/
import { NestedTreeControl } from '@angular/cdk/tree';
import { AfterViewInit, ChangeDetectorRef, Component, NgZone, OnDestroy, OnInit, ViewChild, HostListener } from '@angular/core';
import { MatTreeNestedDataSource } from '@angular/material/tree';
import * as SPREAD_CONFIG_EQUATIONS from '@components/model-data/exogenous-lines-equations/exogenous-lines-equations.spread.config';
import * as GC from '@grapecity/spread-sheets';
import { clone, isNumber } from '@helpers/arrays';
import { clipboardClearOnCut, getClipboardData, getPastedDataMatrix, paste } from '@helpers/clipboard-events';
import { getCurrentTimeInSeconds } from '@helpers/get-current-time-in-seconds';
import { bindSpreadJSEventsToSheet, mapActionKeys } from '@helpers/spread/map-action-keys';
import { resumeAll } from '@helpers/spread/resume-all';
import { suspendAll } from '@helpers/spread/suspend-all';
import { EquationBar } from '@models/equation-bar';
import { EquationsTreeNode } from '@models/equations-tree-node';
import { PeriodEquationMapItem } from '@models/period-equation-map-item';
import { RetrievedPeriodEquations } from '@models/retrieved-period-equations';
import { SpreadSheetHistoryItem } from '@models/spread-sheet-history-item';
import { OpenIdConnectService } from '@services/auth/open-id-connect.service';
import { EquationsService } from '@services/equations/equations.service';
import { ExogenousLinesService } from '@services/exogenous-lines/exogenous-lines.service';
import { MenuService } from '@services/menu/menu.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { TrackingService } from '@services/tracking/tracking.service';
import { setDefaultBookOptions, setDefaultSheetOptions } from '@shared/spreadsheet/base';
import { SpreadContextMenu } from '@shared/spreadsheet/protection';
import { spreadsheetConfig } from '@shared/spreadsheet/spreadsheet-config';
import { ToastLevel } from '@shared/toasterSetup';
import { BehaviorSubject, fromEvent, Subject } from 'rxjs';
import { delay, filter, takeUntil } from 'rxjs/operators';
import { SpreadConfig } from './exogenous-lines-equations.spread.config';
import { PermissionsService } from '@services/auth/permissions.service';
import { GlobalParamsService } from '@services/global-params/global-params.service';
import { adjustOverlaySizeTo } from '@helpers/spread/adjust-overlay-size-to';
import { removeCarraigeAndTabs } from '@helpers/cleanup_equations';
import { removeQuotes } from '@helpers/cleanup_equations';
import { Toast } from 'ngx-toastr';

const SPREADCONFIG = SPREAD_CONFIG_EQUATIONS.SpreadConfig;
const DEBOUNCE_TIME = 600; // (In ms.) Debounce time for service calls to store spredsheet data into DB

@HostListener('window:resize', ['$event'])
@Component({
  selector: 'app-exogenous-lines-equations',
  templateUrl: './exogenous-lines-equations.component.html',
  styleUrls: ['./exogenous-lines-equations.component.scss'],
})
export class ExogenousLinesEquationsComponent implements OnInit, AfterViewInit, OnDestroy {
  /*
      =================
          Properties
      =================
  */

  public viewInitialized = false;
  private destroyed$ = new Subject();
  public readonly spreadConfig = SpreadConfig;

  private spread: GC.Spread.Sheets.Workbook = null;
  private sheet: GC.Spread.Sheets.Worksheet = null;

  public isEditValue = false;
  public showLatest$ = new BehaviorSubject(null);
  public isSaved = false;
  public isSaving = false;
  public failSaving = false;
  public isLoading = true;
  public sheetArea = GC.Spread.Sheets.SheetArea.viewport;
  public isSheetValid$ = new BehaviorSubject(false);
  private exogenousLines$ = new BehaviorSubject(null);
  private storeTempTimeout: NodeJS.Timeout = null;

  // Undo & Redo
  private history: SpreadSheetHistoryItem[] = [];
  private historyCurrentIndex: number;
  private isUndoing = false;
  private isRedoing = false;

  // Invalidations
  public invalidMessages: string[] = [];
  public invalidRows: string[] = [];

  @ViewChild('spreadContainer', { static: false }) spreadContainer;
  @ViewChild('spreadDiv', { static: false }) spreadDiv;
  public spreadContent: object[] = [];
  public equations: object[] = [];
  public selectedExogenousLine$ = new BehaviorSubject(null);
  public selectedScenario = -1; // By now we set -1 for all requests

  // Tree Related
  public lsData: any[];
  public treeData$ = new BehaviorSubject<any[]>([]);
  public nestedDataSource: MatTreeNestedDataSource<any> = new MatTreeNestedDataSource();
  public nestedTreeControl: NestedTreeControl<any>;
  public selectedTreeItem$ = new BehaviorSubject<EquationsTreeNode>(null);
  public selectedItem = null;
  public updatedTables: any = {};

  // Equation Bar
  public equationBar: EquationBar = new EquationBar();
  public displayEquationBar = false;

  // Users Editing
  public editingUsers = <any>[];
  public editingUsersList = '';

  private validating = false;
  private isCutAndPasting = false;

  private periodEquationsMap: PeriodEquationMapItem[] = null;
  private userRoleCanEdit;

  private setSelectedItem = (node: EquationsTreeNode) => {
    // To Do: Simplify similar methods and properties
    this.selectedTreeItem = node;
    this.selectedItem = node.name;
    // console.log('Selected', node);
  };

  /*
    =================
        Property
    Getters && Setters
    =================
  */
  get selectedExogenousLine(): number {
    return this.selectedExogenousLine$.value;
  }

  set selectedExogenousLine(val: number) {
    this.selectedExogenousLine$.next(val);
  }

  get selectedTreeItem() {
    return this.selectedTreeItem$.value;
  }
  set selectedTreeItem(node) {
    if (node) {
      // Request the load of period equations
      if (
        node.name &&
        // !node.children &&
        node.value
      ) {
        const foundObj = this.exoService.sourceIdentifiers.find(elem => elem.mnemonic === node.value) || false;
        if (foundObj && foundObj['Exogenous_Sid'] && typeof foundObj['Exogenous_Sid'] === 'number') {
          this.selectedExogenousLine = foundObj['Exogenous_Sid'];
          this.getPeriodEquations(this.selectedExogenousLine).then(() => {
            // Reset Undo/Redo History
            this.resetUndoRedoHistory();
          });
        } else {
          throw new Error(`There is no sourceIdentifiers associated with the mnemonic of the node selected!: ${node.value}`);
        }
      }

      this.selectedTreeItem$.next(node);
    }
  }

  get isEdit() {
    return this.isEditValue;
  }
  set isEdit(newVal: boolean) {
    this.isEditValue = newVal;
    this.eqService.onEditTimestamp = getCurrentTimeInSeconds();
    // this.setShowLatest();
  }

  get isSheetValid() {
    return this.isSheetValid$.value;
  }
  set isSheetValid(newVal: boolean) {
    this.isSheetValid$.next(newVal);
  }

  get exogenousLines() {
    return this.exogenousLines$.value;
  }
  set exogenousLines(newData) {
    this.exogenousLines$.next(newData);
    // When new Exogenous Lines are received, build the tree
    this.setupTree();
  }

  get tree() {
    return this.treeData$.value;
  }
  set tree(newTree) {
    this.treeData$.next(newTree);
  }

  public saveConfirmationEnabled = () => this.isEdit && this.isSheetValid && this.isSaved && !this.failSaving;

  public saveEnabled = () => this.isEdit && this.isSheetValid && !this.isSaved && this.invalidRows.length === 0;
  public discardEnabled = () => this.isEdit && this.isSheetValid && !this.isSaved && this.invalidRows.length === 0;

  public get buttonsEnabled() {
    return this.saveEnabled() && this.discardEnabled();
  }

  /*
     =================
       Constructor
         & onInit
     =================
 */
  constructor(
    private exoService: ExogenousLinesService,
    private eqService: EquationsService,
    private auth: OpenIdConnectService,
    private trackingService: TrackingService,
    private zone: NgZone,
    private menu: MenuService,
    private changeRef: ChangeDetectorRef,
    private permissions: PermissionsService,
    private params: GlobalParamsService,
    private notificationsService: NotificationsService
  ) {
    this.nestedTreeControl = new NestedTreeControl<any>(this._getChildren);
    this.userRoleCanEdit = this.permissions.getPermission('ModelDataExogenousLinesEquations') === 'W';
  }

  ngOnInit() {
    this.loadData();
    this.changeRef.detectChanges();
  }

  ngAfterViewInit() {
    this.changeRef.detectChanges();
    setTimeout(() => {
      this.viewInitialized = true;
      this.initListeners();
    }, 100);
  }

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private initListeners() {
    this.handleRightClickActions();
  }

  /**
   * Request to the proper service to get the data about exogenous lines
   */
  private loadData() {
    this.getAllExogenousLines().then(() => this.getAllPeriodEquations());
    this.updateTreeOnDataChange();
  }

  /**
   * Gets all the exogenous lines and assigns them to the property exogenousLines
   */
  private async getAllExogenousLines() {
    const exoLines = (await this.exoService.getAllExogenousLines().toPromise()) || [];
    if (exoLines.length > 0) {
      // Assign them
      this.exogenousLines = clone(exoLines);
      this.exoService.sourceIdentifiers = clone(exoLines)
        .map(src => {
          return {
            Exogenous_Sid: src['Exogenous_Sid'],
            mnemonic: src['Mnemonic'],
            Display_Order: src['Display_Order'],
          };
        })
        .sort((a, b) => a.Display_Order < b.Display_Order);
    } else {
      this.exogenousLines = [];
      this.exoService.sourceIdentifiers = [];
    }
  }

  private async getAllPeriodEquations(): Promise<void> {
    this.periodEquationsMap = [];
    if (this.exogenousLines) {
      const allResourceIds = <number[]>this.exogenousLines.map(exoline => <number>exoline['Exogenous_Sid']);

      // Obtain all edited equations for the user and map the result of the query to an array of single values
      const editedEquations: any[] = (await this.trackingService.getUserEditTrackingTable('ExogenousLinesPeriodEquations')).map(entry => {
        if (entry.user_id === this.auth.userId) {
          return entry.resource_id;
        }
      });

      allResourceIds.forEach(async (resourceId: number) => {
        const eqObj = {
          resourceId,
          dataRetrieved: null,
        };
        this.periodEquationsMap.push(eqObj);

        // Add to updatedTables based on the array of edited equations
        if (editedEquations.includes(eqObj.resourceId)) {
          this.addToUpdatedTables(eqObj.resourceId);
        } else {
          this.removeFromUpdatedTables(eqObj.resourceId);
        }
      });
    }
    this.isLoading = false;
  }

  /**
   * Listener that updates the tree when the spreadjs data changes
   */
  private updateTreeOnDataChange() {
    this.treeData$
      // We want to listen to the observable until the component is destroyed
      .pipe(takeUntil(this.destroyed$))
      .subscribe(newData => {
        if (newData && newData.length && newData.length > 0) {
          this.nestedDataSource.data = newData;
          // Expand all nodes of the tree by default in every search or at first opening
          this.expandAllNodes();
        } else {
          this.nestedDataSource.data = null;
        }
      });
  }

  private async getPeriodEquations(resourceId: number): Promise<RetrievedPeriodEquations> {
    const periodEquationsModel = <RetrievedPeriodEquations>(
      ((await this.eqService.getPeriodEquations('ExogenousLinesPeriodEquations', this.auth.userId, resourceId)) || [])
    );

    const periodEquationsModelFound = this.periodEquationsMap.find(pe => pe.resourceId === resourceId) || false;
    if (periodEquationsModelFound) {
      const equations = (periodEquationsModel && periodEquationsModel['equations']) || [];

      if (equations.length > 0) {
        this.equations = clone(equations);
        this.loadInSpreadSheet(this.equations);
        this.exoService.onLoadTimestamp = getCurrentTimeInSeconds();

        // Bug fix: Save button not being displayed when tracking record is loaded (it should be displayed)
        // Added set timeout to avoid changed references in template evaluation
        setTimeout(() => {
          if (
            (periodEquationsModel && periodEquationsModel['isFromTracking'] && equations && equations.length > 0) ||
            this.editingUsers.find(u => u.user_id === this.auth.userId) ||
            false
          ) {
            this.isEdit = true;
            this.isSaved = false;

            // Part of fix 2907
            if (periodEquationsModel['isFromTracking']) {
              this.addToUpdatedTables(this.selectedExogenousLine);
            }
          } else {
            this.isSaved = true;
            this.removeFromUpdatedTables(this.selectedExogenousLine);
          }
          this.isSheetValid = true;

          this.menu.emitResize();
        });
      } else {
        this.isSaved = false;
        this.isEdit = false;
        this.isSheetValid = false;
        this.equations = [];
        console.error(`Any equations retrieved for the resource with ID ${resourceId}`);
      }
    }
    return periodEquationsModel;
  }

  /**
   * Transforms exogenous lines list into a tree
   */
  private setupTree() {
    // ToDo: Consider the change to a forEach instead of a map
    // ---> this.exogenousLines.forEach(item => (item.Group_ID = item.Group_ID !== '' ? item.Group_ID : '[Ungrouped]'));
    this.exogenousLines.map(item => (item.Group_ID = item.Group_ID !== '' ? item.Group_ID : '[Ungrouped]'));

    // tslint:disable-next-line:max-line-length
    // TODO: Notes for bug fix 3205: The problem is we use "names" as keys in the dictionary create inside of the treePrebuild. We should use Exogenous_sid instead. Also inside of treeBuildNodes we need change node.name to: node.name = value; and set te value to what we want to show in the tree (the name? the mnemonic?) I added few comments with some lines modified. Pending of some work to do. Test it in liq. sources p.eq. too and make proper changes
    // const preparedData = this.eqService.treePreBuild(this.exogenousLines, 'Mnemonic', 'Name', 'Group_ID');
    const preparedData = this.eqService.treePreBuild(this.exogenousLines, 'Name', 'Mnemonic', 'Group_ID');

    const newTree = this.eqService.treeBuildNodes(preparedData, 0);

    // Bug Fix 2272
    // This code orders the tree node children by Display_Order. If it's the same Display_Order, it keeps previous ordering (alphabetically)
    newTree.map((treeGroup: EquationsTreeNode) => {
      treeGroup.children.sort((a: EquationsTreeNode, b: EquationsTreeNode) => {
        const aItem = this.exogenousLines.find(item => item.Mnemonic === a.value);
        const bItem = this.exogenousLines.find(item => item.Mnemonic === b.value);
        return aItem.Display_Order - bItem.Display_Order;
      });
    });

    if (newTree && JSON.stringify(newTree) !== JSON.stringify({})) {
      this.tree = newTree;
    }
  }

  /*
     =================
       GC SpreadSheet
          methods
     =================
 */

  /**
   * Initializes the SpreadSheet setup
   */
  public workbookInit(args) {
    this.spread = args.spread;
    this.equationBar.setSpread(this.spread);
    this.setupWorkBook();
  }

  public updateEquationBar = (row: number, col: number, newText: string = null) => {
    if (this.sheet && isNumber(row) && isNumber(col)) {
      this.equationBar.onCellSelected(this.sheet, row, col, newText);
    }
  };

  /**
   * Setup and customize the GC SpreadSheet
   *
   */
  private async setupWorkBook() {
    this.zone.run(() => {
      this.isLoading = true;
      // Set up spreadsheet
      this.sheet = this.spread.getActiveSheet();

      if (this.sheet) {
        mapActionKeys(this.spread, this.sheet, null, this.userRoleCanEdit, false);

        this.equationBar.content$.subscribe(content => this.onFormulaBarNewContent(content));

        this.sheet.suspendPaint();

        // =========================
        //   General Sheet Options
        // =========================
        this.sheet.name('Exog. Lines Period Equations');

        setDefaultSheetOptions(this.sheet.options);
        setDefaultBookOptions(this.spread.options, {
          allowCopyPasteExcelStyle: false,
          tabStripVisible: false,
          allowUndo: false,
        });

        // Context Menu (right-click)
        SpreadContextMenu.setCopyCutPaste(this.spread)
          .disableHideColumns(this.spread)
          .disableHideRows(this.spread)
          .disableInsertColumns(this.spread)
          .disableInsertRows(this.spread)
          .disableDeleteRows(this.spread)
          .disableDeleteColumns(this.spread);

        // Protection Options
        // --- Makes the sheet uneditable (protected) and unlocks only the column where the equations are
        this.sheet.options.isProtected = true;
        this.sheet.options.protectionOptions = {
          allowSelectUnlockedCells: true,
          allowSelectLockedCells: true,
        };

        // 'Period' column locked for everyone
        this.sheet.getRange(0, 0, this.sheet.getRowCount(), 1).locked(true);

        if (this.userRoleCanEdit) {
          // Unlock periods for a big number of rows
          this.sheet.getRange(0, 1, this.sheet.getRowCount(), 1).locked(false);
        } else {
          this.sheet.getRange(0, 1, this.sheet.getRowCount(), 1).locked(true);
        }

        // DATA BINDING
        this.setDataBinding();

        // On cell edit leave, on cell value change, on paste
        this.setupWorkBookEventBindings();

        this.isSaved = false;
        this.isEdit = false;
        this.isLoading = false;
        this.sheet.resumePaint();

        // Select first equation
        this.sheet.setActiveCell(0, 1);
        setTimeout(() => this.updateEquationBar(0, 1));
      }
    });
  }

  public onEditCallback = (event = null, info = null) => {
    if (this.userRoleCanEdit) {
      // Do not include the isEdit inside of onEditedSpreadSheet() because is being call in the first load too
      this.isEdit = true;
      this.onEditedSpreadSheet(info);

      // List needed to ignore this call from the Undo and Redo events
      const eventList = ['CellChanged', 'ClipboardPasted', 'DragDropBlockCompleted', 'DragFillBlockCompleted', 'Delete'];
      if (event && eventList.includes(event.type)) {
        this.refreshUndoRedoHistory();
      }
    }
  };

  // TODO: Adapt pages and this method to be able to use it from an external source (centralized)
  private enableUndoAndRedo() {
    // UNDO: Ctrl + Z
    const undoCommand = () => {
      const undoConditions = this.historyCurrentIndex > 0 && this.history.length > 0;
      if (undoConditions) {
        this.isUndoing = true;
        // console.log('Undoing...');
        // ! Warning:
        // Undo & Redo Events also are spread events so they call the onEditedSpreadsheet.
        // That call adds an Item to the history and increases the historyCurrentIndex, for Undo & Redo we want to avoid that.
        this.historyCurrentIndex--;
        this.loadCurrentHistory();

        this.onEditCallback({ type: 'Undo' });

        setTimeout(() => {
          this.isUndoing = false;
          // console.log('Undone');
        });
      }
    };
    this.spread.commandManager().register('undo', undoCommand, GC.Spread.Commands.Key.z, true, false, false, false);

    // REDO: Ctrl + Y
    const redoCommand = () => {
      const redoConditions =
        // Ensure that the historyCurrentIndex is poiting to an existing index in the array this.history
        // historyCurrentIndex max: this.history.length - 2 , because it should allow to increment it by 1 unit
        this.historyCurrentIndex > -1 && this.historyCurrentIndex < this.history.length - 1;

      if (redoConditions) {
        this.isRedoing = true;
        // console.log('Redoing...');
        // ! Warning:
        // Undo & Redo Events also are spread events so they call the onEditedSpreadsheet.
        // That call adds an Item to the history and increases the historyCurrentIndex, for Undo & Redo we want to avoid that.

        this.historyCurrentIndex++;
        this.loadCurrentHistory();
        this.onEditCallback({ type: 'Redo' });

        setTimeout(() => {
          this.isRedoing = false;
          // console.log('Redone');
        });
      }
    };
    this.spread.commandManager().register('redo', redoCommand, GC.Spread.Commands.Key.y, true, false, false, false);
  }

  private loadCurrentHistory() {
    setTimeout(() => {
      // ! Warning: Timeout needed
      // -- to delay execution and trigger his callback after all the events has ended
      // -- and also to overwrite the default undo/redo functionality of spread
      // Overwrite Undo
      if (this.sheet) {
        suspendAll(this.sheet);
        // Looping to avoid reassignement that would break data binding
        this.spreadContent.forEach((row, index) => (this.spreadContent[index] = clone(this.history[this.historyCurrentIndex].data[index])));
        resumeAll(this.sheet);
        this.sheet.repaint();
        this.menu.emitResize();
      }
    });
  }

  /**
   * Event listeners for the spreadsheet
   *
   */
  private setupWorkBookEventBindings() {
    bindSpreadJSEventsToSheet(this.sheet);
    // Trigger on cell change
    this.sheet.bind(GC.Spread.Sheets.Events.CellChanged, (event, info) => {
      // Filter events to only validate when value is modified (but not when the cell style changes)

      if (info.propertyName === 'value') {
        this.onEditCallback(event, info);
        const { row, col } = info;
        this.updateEquationBar(row, col);
      }
    });

    // Trigger on pasting data
    // ! WARNING Fix 2459:
    // -- Equations can now be correctly copied and pasted (including one value to many cells)
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasting, (event, info) => {
      if (this.userRoleCanEdit) {
        //fix for defect 5844. Undone as it caused defect 6227
        /*info.pasteData.text = removeCarraigeAndTabs(info.pasteData.text);
        info.pasteData.text = removeQuotes(info.pasteData.text);*/
        // console.log("pasting triggered")
        // if is a cut event, it clears the clipboard content once the event ends
        clipboardClearOnCut(info, this.sheet);

        const isCutAndPasteEvent = info.pasteOption === 0;
        const isCopyAndPasteEvent = info.pasteOption === 1;
        // console.log(
        //   `It is ${
        //     isCopyAndPasteEvent ? 'a copy-and-paste' : isCutAndPasteEvent ? 'a cut-and-paste' : 'an INVALID'
        //   } paste event`
        // );

        if (isCutAndPasteEvent) {
          this.isCutAndPasting = isCutAndPasteEvent;
        }
        // ! Other actions are executed from the keyup listener of Ctrl+V

        if (this.sheet.getSelections().length === 0) {
          const { row, col } = this.equationBar.activeCell;
          this.sheet.setActiveCell(row, col);
        }

        if (info.pasteData.text) {
          const selection = this.sheet.getSelections()[0];
          // ! Fix 4412
          if (selection && selection.row < 0) selection.row = 0;
          if (selection && selection.col < 0) selection.col = 0;

          const pastedCells = getPastedDataMatrix(info.pasteData.text.trim());

          // Update cell values with pasted data
          let value: string;
          const max = Math.max(selection.rowCount, pastedCells.length);
          const c = 0;
          suspendAll(this.sheet);
          for (let r = 0, p = 0; r < max; r++, p++) {
            // It will iterate over the array of selected rows (r) and the array of pasted data (p)
            // If the selection is bigger than the array of pasted data, when the array of pasted data is finished
            // it will start over. If the pasted data is bigger than the array of selected rows, it will spill out the selection
            const row = selection.row + r;
            const col = selection.col + c;

            if (p > pastedCells.length - 1) {
              p = 0;
            }
            value = pastedCells[p][0];

            this.sheet.getCell(row, col).value(value);
          }
          resumeAll(this.sheet);

          // Avoid the Ctrl+X and Ctrl+V to break with formulas converting them to #NAME?
          // ! Fix 2459 (#NAME? problem): Avoiding invalidation of cells on paste of equations with
          // ! undefined formulas as for example =EndBal(LT_LOANS)/EndBal(LT_DEPOSITS)
          const hasEquationInPaste = getPastedDataMatrix(info.pasteData.text)
            .flat()
            .find(cell => cell.indexOf('=') === 0);
          if (hasEquationInPaste) {
            console.log(`ClipboardPasted preventDefault() triggered to avoid the conversion of the field to #NAME?`);
            event.preventDefault();
          }
        }
      }
    });

    // ! It is not executed because of the event.preventDefault
    this.sheet.bind(GC.Spread.Sheets.Events.ClipboardPasted, (event, info) => {
      if (this.userRoleCanEdit) {
        // console.log(`📋 ClipboardPasted`, { event: clone(event), info: clone(info) });
        // this.onEditeCallback(event, info);
      }
    });

    // Trigger on drag and drop values
    this.sheet.bind(GC.Spread.Sheets.Events.DragDropBlockCompleted, (event, info) => this.onEditCallback(event, info));

    // Trigger on drag and fill values
    this.sheet.bind(GC.Spread.Sheets.Events.DragFillBlockCompleted, (event, info) => {
      this.onEditCallback(event, info);

      // ! Fix for defects 4044 and 4675
      const targetSpreadContainer = document.querySelector('.panel-main-body .spread-container') as HTMLElement;
      adjustOverlaySizeTo(targetSpreadContainer);
    });

    // Bug fix 2434 & 3224
    this.enableUndoAndRedo();

    // Delete Key Mapping (Setting a value by default when deleted)
    this.addDeleteKeyBinding();

    // Formula Bar
    this.sheet.bind(GC.Spread.Sheets.Events.EditChange, (event, info) => {
      const { row, col, editingText: newText } = info;
      this.updateEquationBar(row, col, newText);
    });

    this.sheet.bind(GC.Spread.Sheets.Events.EnterCell, (event, info) => {
      const { row, col } = info;
      this.updateEquationBar(row, col);
    });

    this.sheet.bind(GC.Spread.Sheets.Events.EditEnded, (event, info) => {
      const { row, col } = info;
      this.updateEquationBar(row, col);
    });
  }

  private clipboardPastedCallback(event, info = null) {
    if (this.userRoleCanEdit) {
      // Callback of clipboardPasting because the regular event is prevented
      const necessaryDelay = 100;
      const selection: GC.Spread.Sheets.CellRange = <any>this.sheet.getSelections()[0];
      if (selection) {
        info = {
          pasteOption:
            info && typeof info.pasteOption === 'number' && info.pasteOption > -1 ? info.pasteOption : this.isCutAndPasting ? 0 : 1,
        };

        // -- We need to avoid that the pasted values are converted to formulas by SpreadJS so we bypass the ClipboardPasted event
        // -- Timeout needed for a correct update of the undoRedoHistory
        setTimeout(async () => {
          // Paste over those cells
          // ! Defect 4116
          // const pastedText = await getClipboardData();
          // paste(this.sheet, selection, pastedText);
          info = { ...info, sheet: this.sheet };
          this.onEditCallback({ type: 'ClipboardPasted' }, info);
          this.isCutAndPasting = false;
        }, 0);

        // Update equation bar on paste
        setTimeout(() => {
          const { row, col } = selection;
          this.updateEquationBar(row, col);
        }, necessaryDelay + 60);
      } else {
        console.warn(event);
        console.log(this.sheet.getSelections());
      }
    }
  }

  // Delete key workaround
  private addDeleteKeyBinding() {
    const actionsOnDelete = () => {
      // On delete key pressed, if any selection contains column 0 (Period) we stop the deletion
      const selections = this.sheet.getSelections();
      const selectedCols = selections.map(s => s.col);
      const shouldStop = selectedCols.filter(s => s < 1).length > 0; // col === 0 or -1

      if (!this.sheet.isEditing() && !shouldStop) {
        // Timeout needed to wait until events end
        setTimeout(() => {
          this.onEditCallback({ type: 'Delete' });
          const { row, col } = this.equationBar.activeCell;
          this.updateEquationBar(row, col);
        });
      }
    };

    const deleteCommand = {
      canUndo: true,
      execute: function (context, options, isUndo) {
        const Commands = GC.Spread.Sheets.Commands;
        if (isUndo) {
          // Undo feature for 'Delete' key only
          Commands.undoTransaction(context, options);
          return true;
        } else {
          Commands.startTransaction(context, options);
          actionsOnDelete();
          Commands.endTransaction(context, options);
          return false;
        }
      },
    };
    const commandManager = this.spread.commandManager();
    if (this.userRoleCanEdit) {
      commandManager.register('deleteKey', deleteCommand, GC.Spread.Commands.Key.del);
    }
  }

  private refreshUndoRedoHistory() {
    // ! Timeout applied to trigger it after any spreadsheet event ends
    // -- (example: pasting is not equal to pasted). Can be improved using event queues and observables
    setTimeout(() => {
      if (this.spread) {
        this.spread.undoManager().clear();
      }

      const historyObject: SpreadSheetHistoryItem = {
        date: new Date().getTime(),
        data: clone(this.spreadContent), // TODO: Improve it using spread.toJSON instead of using raw data
      };

      if (!(this.isUndoing || this.isRedoing)) {
        // Add the latest state after the current index
        // and removing all the other potential redo states
        // const shouldRemoveRedoHistory = this.historyCurrentIndex >= 0 && this.historyCurrentIndex < this.history.length - 1;

        const endIndex = this.history.length;
        const startIndex = this.historyCurrentIndex + 1;
        this.history.splice(startIndex, endIndex, historyObject);
        this.historyCurrentIndex++;
        const { row, col } = this.equationBar.activeCell;
        this.updateEquationBar(row, col);
      }
    });
  }

  /**
   * It is triggered when some edition of the spreadsheet happens.
   * It purges the values of the fields and calls the content validity checker
   */
  public onEditedSpreadSheet(info = null) {
    // Do not store temp if there's been a single change and it's the same as the old change

    if (info && info.hasOwnProperty('newValue') && info.hasOwnProperty('oldValue')) {
      info.newValue = info.newValue || '';
      info.oldValue = info.oldValue || '';
      if (info.newValue === info.oldValue) {
        return;
      }
    }
    // Grab the details of the sheet tha triggered the change instead of the current state of the sheet (can be different)
    const updatedSheet: GC.Spread.Sheets.Worksheet = info ? info.sheet : null;
    try {
      this.zone.run(() => {
        if (this.sheet) {
          this.isLoading = true;
          this.isSheetValid = false;
          this.isSaved = false;

          this.mapEquationsIntoSpreadCells();

          // Temporary Store
          if (this.isEdit) {
            if (updatedSheet) {
              const contentToUpdate = updatedSheet.getDataSource().map(row => this.eqService.parsePeriodEquationsRow(row));
              this.storeTemp(contentToUpdate);
            } else {
              this.storeTemp();
            }
          }

          // We force it to be true in this page because we
          // do not validate the sheet
          this.isSheetValid = true;
        }
      });
    } catch (err) {
      console.error(err);
    }
  }

  private mapEquationsIntoSpreadCells() {
    this.zone.run(() => {
      if (this.sheet) {
        suspendAll(this.sheet);
        this.spreadContent.map(row => this.eqService.parsePeriodEquationsRow(row));
        resumeAll(this.sheet);
        this.isLoading = false;
      }
    });
  }

  private onFormulaBarNewContent(content: string) {
    if (content) {
      const { row, col } = this.equationBar.activeCell;
      this.sheet.suspendEvent();
      this.sheet.suspendPaint();
      const previousValue = this.sheet.getCell(row, col).value();
      if (previousValue !== content) {
        this.sheet.getCell(row, col).value(content);
        this.isEdit = true;
        this.onEditedSpreadSheet();
        this.menu.emitResize();
      }

      this.sheet.resumePaint();
      this.sheet.resumeEvent();
    }
  }

  /**
   * Updates the data of the spreadsheet
   *
   */
  private setDataBinding() {
    if (this.sheet) {
      this.zone.run(() => {
        this.sheet.autoGenerateColumns = true;
        this.sheet.setDataSource(this.spreadContent);
        this.sheet.bindColumns(SPREADCONFIG.columnsConfig);
        const rowCount = this.sheet.getRowCount();
        const colCount = this.sheet.getColumnCount();
        this.sheet.setColumnCount(colCount);
        this.sheet.setRowCount(rowCount || this.sheet.getDataItem.length);
        this.sheet.defaults.rowHeight = SPREADCONFIG.DEFAULT_VALUES.rowHeight;
        this.sheet.defaults.colWidth = SPREADCONFIG.DEFAULT_VALUES.colWidth;

        const { row, col } = this.equationBar.activeCell;
        this.updateEquationBar(row, col);
      });
    }
  }

  /*
 =================
     Other
     methods
 =================
 */

  /*
   ** Data-related actions: Save
   */

  /**
   * Calls the service to store permanently the data in the database (SAVE)
   */
  public async saveData() {
    this.isSaving = true;

    try {
      const result = await this.eqService.savePeriodEquations(
        'ExogenousLinesPeriodEquations',
        this.spreadContent,
        this.selectedExogenousLine
      );

      // TODO: Refactorize API microservice for consistency:
      // Note: result is true or false for exogenous lines period equations (but not the same as liq.src period equations)
      if (result) {
        this.removeFromUpdatedTables(this.selectedExogenousLine);
        this.failSaving = false;
        this.isSaved = true;
        this.resetUndoRedoHistory();
        
        // Show success toast
        const selectedExogenousLineName = this.selectedTreeItem?.name || 'Exogenous Line';
        this.notificationsService.showToast(
          ToastLevel.Success,
          { title: 'Changes Saved Successfully', message: `Period equations for ${selectedExogenousLineName} have been saved successfully.` } as Toast
        );
      } else {
        this.failSaving = true;
        const errorMessage = `Something failed trying to save changes in the database: ${result}`;
        console.error(errorMessage);
        console.error('Error saving changes in the database');
        this.notificationsService.showToast(
          ToastLevel.Error,
          { title: 'Save Failed', message: errorMessage } as Toast
        );
      }
    } catch (err) {
      const errorMessage = `Something failed trying to save changes in the database:`;
      console.error(errorMessage, err);
      this.notificationsService.showToast(
        ToastLevel.Error,
        { title: 'Save Failed', message: errorMessage } as Toast
      );
    } finally {
      this.isSaving = false;
    }
  }

  // Used to reset the undo/redo history
  private resetUndoRedoHistory() {
    if (this.spread) {
      this.spread.undoManager().clear();
    }

    // Add the current state to the history as the only one
    this.history = [];
    this.historyCurrentIndex = -1;
    setTimeout(() => this.refreshUndoRedoHistory());
  }

  public async discardTemp() {
    const result = await this.eqService.deletePeriodEquationsTrackingItems('ExogenousLinesPeriodEquations', this.selectedExogenousLine);
    if (result) {
      this.failSaving = false;
      this.isSaved = false;
      this.removeFromUpdatedTables(this.selectedExogenousLine);
    } else {
      this.failSaving = true;
      const errorMessage = `Something failed trying to discard changes from the database`;
      console.error(errorMessage, result);
      console.error(errorMessage);
      this.notificationsService.showToast(
        ToastLevel.Error,
        { title: 'Discard Failed', message: errorMessage } as Toast
      );
    }

    this.getPeriodEquations(this.selectedExogenousLine).then(() => {
      // Reset Undo/Redo History
      this.resetUndoRedoHistory();
    });
  }

  /**
   * Saves a tracking item of the liquidity sources period equations for that user
   */
  private storeTemp(forceSpreadContent = null) {
    if (this.storeTempTimeout) {
      clearTimeout(this.storeTempTimeout);
      this.storeTempTimeout = null;
    }

    this.isSaving = true;

    this.storeTempTimeout = setTimeout(async () => {
      try {
        let content = forceSpreadContent ? forceSpreadContent : this.spreadContent;
        const sourceSidToStore = forceSpreadContent && content ? content[0]['ExogenousLineID'] : this.selectedExogenousLine;

        // Check that the maximum amount of equations we store are Periods + 1 (period 0 included)
        const max_periods = this.params.numPeriods + 1;
        if (content.length > max_periods) {
          // Try to remove duplicates to solve this incorrect state
          const spreadContentWithoutDupes = content.filter(element => element === content.find(ele => ele['Period'] === element['Period']));

          // If it's still above the limit, throw exception
          if (spreadContentWithoutDupes.length > max_periods) {
            throw {
              name: 'Object too large',
              message: `The set of equations you're trying to create is too large. Please refresh the window and try again.`,
            };
          } else {
            content = spreadContentWithoutDupes;
          }
        }

        const storeTemp = await this.eqService.storeTempPeriodEquations('ExogenousLinesPeriodEquations', content, sourceSidToStore);

        delete storeTemp['source_sid'];

        if (storeTemp && storeTemp['date_time_created'] && storeTemp['table_data']) {
          this.exoService.onLoadTimestamp = getCurrentTimeInSeconds();
          this.isEdit = true;
          this.addToUpdatedTables(sourceSidToStore);
        } else {
          console.error(
            `Check the back-end. Error creating the temporary record for the period equations of the liquidity source with ID:`,
            sourceSidToStore
          );
        }
      } catch (err) {
        if (err.name !== 'Object too large') {
          console.error(
            `Check the back-end. Error creating the temporary record for the period equations of the liquidity source with ID:`,
            this.selectedExogenousLine
          );
        }
        console.error(err);
      } finally {
        this.isSaving = false;
      }
    }, DEBOUNCE_TIME);
  }

  /*
   ** TREE-related methods
   */
  private _getChildren = (node: any) => node.children;

  public isItemSelected = (name: string) => name === this.selectedItem;

  public isExpandedNode = (node: EquationsTreeNode = null) => (node ? this.nestedTreeControl.isExpanded(node) : false);

  public hasNestedChild = (_: number, node: EquationsTreeNode = null) => {
    return node && !node.value && node.children.length > 0 ? true : false;
  };

  public selectNode(node: EquationsTreeNode = null) {
    if (node) {
      this.expandNode(node);
      this.setSelectedItem(node);
    }
  }

  public toggleNode(node: EquationsTreeNode = null): void {
    if (node /*&& node.children && node.children.length > 0*/) {
      this.nestedTreeControl.toggle(node);
    }
  }

  public expandNode = (node: EquationsTreeNode = null): void => {
    if (node) {
      this.nestedTreeControl.expand(node);
    }
  };

  public collapseNode = (node: EquationsTreeNode = null): void => {
    if (node) {
      this.nestedTreeControl.collapse(node);
    }
  };

  public expandDescendants(targetNode: EquationsTreeNode) {
    if (targetNode) {
      // this.nestedTreeControl.expandDescendants(rootNode);
      targetNode.children.forEach(children => this.nestedTreeControl.expand(children));
      // console.log('Tree expanded recursively');
    }
  }

  /**
   * Expands all the nodes of the tree
   */
  private expandAllNodes() {
    this.nestedTreeControl.dataNodes = this.tree;
    this.nestedTreeControl.expandAll();
    // console.log('Expanding all nodes of the tree');
  }

  /**
   * Emits the next value as the node selected by the user in the tree
   */
  public onSelectTreeNode(node) {
    if (node) {
      this.selectedTreeItem = node;
    }
  }

  // Others
  /**
   * Loads data into the Spreadsheet
   */
  private loadInSpreadSheet(equations: object[]): void {
    if (equations) {
      this.resetMappings();
      this.zone.run(() => {
        this.isLoading = true;
        this.isSheetValid = false;
        this.isSaved = false;
        this.isEdit = false;

        if (!equations || JSON.stringify(equations) === '[]') {
          equations = [];
          this.equations = equations;
        }
        this.spreadContent = clone(equations);
        this.setDataBinding();
        this.mapEquationsIntoSpreadCells();

        if (this.sheet) {
          this.sheet.clearSelection();
          this.sheet.setActiveCell(0, 1);
          this.updateEquationBar(0, 1);
        }
        this.isLoading = false;

        // ! Timeout needed
        // -- to delay the execution until events end and view has been checked
        setTimeout(() => {
          this.displayEquationBar = true;
          this.spread.focus();
        });
      });
    }
  }

  private resetMappings() {
    this.failSaving = false;
  }

  // Edit notifier for the tree
  public isTableModified = (mnemonic: string) => this.updatedTables[mnemonic];

  private addToUpdatedTables = (resource_id: number) => {
    const modifiedMnemonic = this.exogenousLines.filter(item => item['Exogenous_Sid'] === resource_id).map(item => item['Mnemonic']);
    this.updatedTables[modifiedMnemonic] = true;
  };

  private removeFromUpdatedTables = (resource_id: number) => {
    const modifiedMnemonic = this.exogenousLines.filter(item => item['Exogenous_Sid'] === resource_id).map(item => item['Mnemonic']);
    try {
      delete this.updatedTables[modifiedMnemonic];
    } catch (err) {}
  };
  // End of edit notifier for the tree

  private handleRightClickActions() {
    const actions = {
      copy: 'gc-spread-copy',
      cut: 'gc-spread-cut',
      paste: 'gc-spread-paste',
    };
    const container = document.getElementById(spreadsheetConfig.containerParentId);

    const rightClick$ = fromEvent(container, 'mouseup');
    // Right Click pasted and ctrl+v
    const pasted$ = fromEvent(document.getElementById(spreadsheetConfig.containerParentId), 'keyup');

    rightClick$
      .pipe(
        takeUntil(this.destroyed$),
        filter((e: MouseEvent) => e['which'] === 3),
        delay(50)
      )
      .subscribe((rightClickEvent: MouseEvent) => {
        const sheet: GC.Spread.Sheets.Worksheet = this.spread.getActiveSheet();
        if (sheet) {
          // Attach the event listener to the container and not to other element
          const clickListener = async (event: MouseEvent) => {
            const eventType = event.target['parentNode'].innerText.toLowerCase();
            if (Object.keys(actions).includes(eventType)) {
              if (eventType === 'paste') {
                // PASTE
                // -----
                this.clipboardPastedCallback(event);
              }
            }
            container.removeEventListener('click', clickListener);
          };

          // Remove any eventlistener at the start
          container.addEventListener('click', clickListener);
        }
      });

    pasted$.pipe(takeUntil(this.destroyed$)).subscribe((event: KeyboardEvent) => {
      const pasted = event && ['KeyV'].includes(event.code) && event.ctrlKey; // It emits as Ctrl+V
      if (pasted && this.userRoleCanEdit) {
        this.clipboardPastedCallback(event);
      }
    });
  }
}
